import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, logConversion } from "./api";
import { useAuth } from "./AuthContext";

const UpgradeContext = createContext(null);
export const useUpgrade = () => useContext(UpgradeContext);

const isTyping = () => {
  const el = document.activeElement;
  if (!el) return false;
  return el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable;
};

export function computeMetrics(data) {
  if (!data) return [];
  const { usage = {}, limits = {} } = data;
  const rows = [];
  const push = (key, label, used, limit, unit = "") => {
    if (limit === -1 || limit == null) return; // unlimited → skip
    rows.push({
      key, label, used, limit, unit,
      pct: Math.min(100, Math.round((used / Math.max(1, limit)) * 100)),
      remaining: Math.max(0, +(limit - used).toFixed(2)),
    });
  };
  push("ai_questions", "AI Coach questions", usage.ai_questions || 0, limits.ai_questions);
  push("report_analyses", "Report analyses", usage.report_analyses || 0, limits.report_analyses);
  push("storage", "Document storage", usage.storage_used_mb || 0, limits.storage_mb, "MB");
  return rows;
}

export const UpgradeProvider = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const isFree = !!user && user.plan === "free";

  const [data, setData] = useState(null);
  const [banner, setBanner] = useState(false);
  const [modal, setModal] = useState({ open: false, metric: null, trigger: "" });

  const autoShownRef = useRef(sessionStorage.getItem("cc_upg_modal_seen") === "1");
  const bannerDismissedRef = useRef(sessionStorage.getItem("cc_upg_banner_dismissed") === "1");

  const refresh = useCallback(async () => {
    if (!user || user.plan !== "free") { setData(null); setBanner(false); return; }
    try { const res = await api.get("/subscription"); setData(res.data); } catch (e) { console.error("usage refresh failed", e); }
  }, [user]);

  const openModal = useCallback((trigger, metric, { force = false } = {}) => {
    if (!force && autoShownRef.current) return;
    autoShownRef.current = true;
    sessionStorage.setItem("cc_upg_modal_seen", "1");
    setBanner(false);
    setModal({ open: true, metric: metric || null, trigger });
    logConversion("impression", { trigger: "modal", metric: metric || "" });
  }, []);

  const closeModal = useCallback(() => {
    setModal((m) => { if (m.open) logConversion("dismiss", { trigger: "modal", metric: m.metric || "" }); return { open: false, metric: null, trigger: "" }; });
  }, []);

  const dismissBanner = useCallback(() => {
    bannerDismissedRef.current = true;
    sessionStorage.setItem("cc_upg_banner_dismissed", "1");
    setBanner(false);
    logConversion("dismiss", { trigger: "banner" });
  }, []);

  const startCheckout = useCallback(async (plan, metric, cycle = "monthly") => {
    logConversion("checkout_click", { trigger: "modal", metric: metric || "", meta: { plan, cycle } });
    try {
      const { data: res } = await api.post("/payments/checkout", {
        plan, billing_cycle: cycle, origin_url: window.location.origin, source: "upgrade_prompt",
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
    }
  }, []);

  // Evaluate usage → banner / auto-modal
  useEffect(() => {
    if (!data || !isFree) { setBanner(false); return; }
    const rows = computeMetrics(data);
    const maxPct = rows.reduce((m, r) => Math.max(m, r.pct), 0);
    const maxed = rows.filter((r) => r.pct >= 100);
    if (maxed.length && !autoShownRef.current && !isTyping()) {
      openModal("limit_auto", maxed[0].key);
      return;
    }
    setBanner(maxPct >= 80 && !bannerDismissedRef.current && !autoShownRef.current);
  }, [data, isFree, openModal]);

  // Refresh triggers
  useEffect(() => { refresh(); }, [refresh, location.pathname]);

  useEffect(() => {
    const onUsage = () => refresh();
    const onLimit = (e) => {
      refresh();
      openModal("limit_" + (e.detail?.metric || "hit"), e.detail?.metric, { force: true });
    };
    window.addEventListener("cc:usage-changed", onUsage);
    window.addEventListener("cc:limit-reached", onLimit);
    return () => {
      window.removeEventListener("cc:usage-changed", onUsage);
      window.removeEventListener("cc:limit-reached", onLimit);
    };
  }, [refresh, openModal]);

  const metrics = computeMetrics(data);
  const value = { isFree, data, metrics, banner, modal, openModal, dismissBanner, closeModal, startCheckout, refresh };
  return <UpgradeContext.Provider value={value}>{children}</UpgradeContext.Provider>;
};
